import { c as _c } from "react/compiler-runtime";
import { InfiniteData, QueryClient, keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Unit } from "@/types/enums/unit";
import { Shipment } from "@/types/inventory/shipment";
import { AddStock, DrawdownStock, ItemStock, Stock, StockDetail, StockFilters, StockLocationLevel, SupplierToStockCreate, SupplierToStockUpdate } from "@/types/inventory/stock";
import { Page } from "@/types/pagination";
import { itemQueryKeys } from "@/api/provider/inventory/item-query";
import { createShipments, deleteShipment, updateShipments } from "@/api/provider/inventory/shipment";
import { addStock, archiveStock, drawdownStock, getStock, getStockStats, linkStockToItem, linkSupplierToStock, listStockGroups, listStocks, unlinkStockFromItem, unlinkSupplierFromStock, updateStocks } from "@/api/provider/inventory/stock";

// ─────────────────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────────────────

export const stockQueryKeys = {
  all: ["inventory", "stock"] as const,
  list: (filters?: StockFilters) => [...stockQueryKeys.all, "list", filters] as const,
  groups: (filters?: StockFilters) => [...stockQueryKeys.all, "groups", filters] as const,
  detail: (id: string) => [...stockQueryKeys.all, "detail", id] as const
};

// ─────────────────────────────────────────────────────────────────────────────
// Query Options
// ─────────────────────────────────────────────────────────────────────────────

export const stockQueryOptions = {
  list: (filters?: StockFilters) => ({
    queryKey: stockQueryKeys.list(filters),
    queryFn: () => listStocks(filters ?? {})
  }),
  groups: (filters: StockFilters) => ({
    queryKey: stockQueryKeys.groups(filters),
    queryFn: () => listStockGroups(filters)
  }),
  detail: (id: string, queryClient?: QueryClient) => ({
    queryKey: stockQueryKeys.detail(id),
    queryFn: () => getStock(id),
    // A list row is only a preview: its locationSummaries are scoped to the
    // list's location filter, so it must never be cached as the detail.
    ...(queryClient && {
      placeholderData: () => findStockInListCaches(queryClient, id)
    })
  }),
  stats: (id: string, startDate?: string, endDate?: string) => ({
    queryKey: [...stockQueryKeys.detail(id), "stats", startDate, endDate] as const,
    queryFn: () => getStockStats(id, startDate, endDate)
  })
};

// ─────────────────────────────────────────────────────────────────────────────
// Cache Helpers
// ─────────────────────────────────────────────────────────────────────────────

type InfiniteStockCache = InfiniteData<Page<Stock>>;
type PaginatedStockCache = Page<Stock>;
type StockListCache = InfiniteStockCache | PaginatedStockCache;

/**
 * Checks if the cache data is from useInfiniteQuery (has pages array).
 */
function isInfiniteCache(data: StockListCache): data is InfiniteStockCache {
  return "pages" in data && Array.isArray(data.pages);
}

/**
 * Finds a stock by ID in any list cache format (infinite or paginated).
 */
function findStockInCache(cache: StockListCache, id: string): Stock | undefined {
  if (isInfiniteCache(cache)) {
    return cache.pages?.flatMap(page => page?.data)?.find(s => s?.id === id);
  }
  return cache.data?.find(s => s?.id === id);
}
function findStockInListCaches(queryClient: QueryClient, id: string): Stock | undefined {
  const listCaches = queryClient.getQueriesData<StockListCache>({
    queryKey: [...stockQueryKeys.all, "list"]
  });
  for (const [, data] of listCaches) {
    const match = data && findStockInCache(data, id);
    if (match) return match;
  }
}

/**
 * Copies the core (non-relational) stock fields from a freshly-returned server
 * Stock onto a cached object, leaving detail-only relations untouched. The
 * update endpoint doesn't necessarily return items in their richer detail shape
 * (StockItemDetail with nested Item) or the full shipment/supplier/barcode
 * lists, so we only patch the scalar fields the edit form can change.
 */
function applyStockCoreFields<T extends Stock>(target: T, source: Stock, locationIds?: string[]): T {
  return {
    ...target,
    name: source.name,
    description: source.description,
    unit: source.unit,
    notes: source.notes,
    category: source.category,
    minProviderAllocationLevel: source.minProviderAllocationLevel,
    locationLevels: source.locationLevels,
    locationSummaries: withLevelsFrom(target.locationSummaries, source, locationIds),
    isArchived: source.isArchived
  };
}

/**
 * Cached summaries can be scoped to a location filter, so their quantities are
 * kept and only their levels refreshed. A location the saved stock now reports
 * (a level set where nothing was delivered) is added when it is in scope.
 */
function withLevelsFrom(summaries: Stock["locationSummaries"], source: Stock, locationIds?: string[]): Stock["locationSummaries"] {
  if (!summaries || !source.locationSummaries) return summaries;
  const saved = new Map(source.locationSummaries.map(summary => [summary.locationId, summary]));
  const cachedIds = new Set(summaries.map(summary => summary.locationId));
  const added = source.locationSummaries.filter(summary => !cachedIds.has(summary.locationId) && (!locationIds?.length || locationIds.includes(summary.locationId)));
  return [...summaries.map(summary => ({
    ...summary,
    minStockLevel: saved.get(summary.locationId)?.minStockLevel ?? null,
    maxStockLevel: saved.get(summary.locationId)?.maxStockLevel ?? null
  })), ...added];
}

/**
 * Patches a single stock's core fields in any list cache format (infinite or
 * paginated), returning a new cache object. Non-matching rows are left as-is.
 */
function patchStockInCache(cache: StockListCache, updated: Stock, locationIds?: string[]): StockListCache {
  if (isInfiniteCache(cache)) {
    return {
      ...cache,
      pages: cache.pages.map(page => ({
        ...page,
        data: page.data.map(s => s.id === updated.id ? applyStockCoreFields(s, updated, locationIds) : s)
      }))
    };
  }
  return {
    ...cache,
    data: cache.data.map(s => s.id === updated.id ? applyStockCoreFields(s, updated, locationIds) : s)
  };
}

/**
 * Removes a stock by ID from any list cache format (infinite or paginated).
 * Returns a new cache object with the stock filtered out.
 */
function removeStockFromCache(cache: StockListCache, id: string): StockListCache {
  if (isInfiniteCache(cache)) {
    return {
      ...cache,
      pages: cache.pages.map(page => ({
        ...page,
        data: page.data.filter(s => s.id !== id)
      }))
    };
  }
  return {
    ...cache,
    data: cache.data.filter(s => s.id !== id),
    total: cache.total - 1
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Query Hooks
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_PER_PAGE = 20;
interface UseStocksOptions {
  filters?: StockFilters;
  enabled?: boolean;
}
interface UseStocksResult {
  stocks: StockDetail[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  totalStocks: number;
}

/**
 * Hook to fetch stocks with infinite loading support.
 * Accepts any StockFilters for flexible querying.
 */
export function useStocks({
  filters,
  enabled = true
}: UseStocksOptions = {}): UseStocksResult {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: stockQueryKeys.list(filters),
    queryFn: async ({
      pageParam = 1
    }) => {
      const queryFilters: StockFilters = {
        ...filters,
        page: pageParam,
        perPage: filters?.perPage ?? DEFAULT_PER_PAGE,
        sortBy: filters?.sortBy ?? "name",
        sortDirection: filters?.sortDirection ?? "asc"
      };
      const response = await listStocks(queryFilters);
      return response ?? {
        data: [],
        total: 0
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.data.length, 0);
      if (loadedCount < lastPage.total) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled,
    staleTime: 30000 // Consider data fresh for 30 seconds
  });

  // Flatten all pages into a single array of stocks
  const stocks = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page_0 => page_0.data) as StockDetail[];
  }, [data?.pages]);
  const totalStocks = data?.pages?.[0]?.total ?? 0;
  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return {
    stocks,
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage: handleFetchNextPage,
    refetch,
    totalStocks
  };
}
interface UseStocksPaginatedOptions {
  filters?: StockFilters;
  enabled?: boolean;
}
interface UseStocksPaginatedResult {
  stocks: StockDetail[];
  isLoading: boolean;
  totalStocks: number;
  refetch: () => void;
}

/**
 * Hook to fetch stocks with traditional pagination support (prev/next).
 * Uses the filters.page value directly for page-based navigation.
 * Use this for table views with pagination controls.
 * For infinite scrolling (load more), use useStocks instead.
 */
export function useStocksPaginated(t0) {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    filters,
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[2] !== filters) {
    t3 = stockQueryKeys.list(filters);
    $[2] = filters;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== filters) {
    t4 = async () => {
      const queryFilters = {
        ...filters,
        page: filters?.page ?? 1,
        perPage: filters?.perPage ?? DEFAULT_PER_PAGE,
        sortBy: filters?.sortBy ?? "name",
        sortDirection: filters?.sortDirection ?? "asc"
      };
      const response = await listStocks(queryFilters);
      return response ?? {
        data: [],
        total: 0
      };
    };
    $[4] = filters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== enabled || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled,
      staleTime: 600000,
      gcTime: 600000
    };
    $[6] = enabled;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data,
    isLoading,
    refetch
  } = useQuery(t5);
  let t6;
  if ($[10] !== data?.data) {
    t6 = data?.data ?? [];
    $[10] = data?.data;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = t6 as StockDetail[];
  const t8 = data?.total ?? 0;
  let t9;
  if ($[12] !== isLoading || $[13] !== refetch || $[14] !== t7 || $[15] !== t8) {
    t9 = {
      stocks: t7,
      isLoading,
      totalStocks: t8,
      refetch
    };
    $[12] = isLoading;
    $[13] = refetch;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
export function useStock(id) {
  const $ = _c(6);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== id || $[1] !== queryClient) {
    t0 = stockQueryOptions.detail(id, queryClient);
    $[0] = id;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = !!id;
  let t2;
  if ($[3] !== t0 || $[4] !== t1) {
    t2 = {
      ...t0,
      enabled: t1,
      staleTime: 600000,
      gcTime: 600000
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return useQuery(t2);
}
export function useStockStats(id, startDate, endDate) {
  const $ = _c(7);
  let t0;
  if ($[0] !== endDate || $[1] !== id || $[2] !== startDate) {
    t0 = stockQueryOptions.stats(id, startDate, endDate);
    $[0] = endDate;
    $[1] = id;
    $[2] = startDate;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = !!id;
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      ...t0,
      enabled: t1,
      placeholderData: keepPreviousData
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
}
export function useInvalidateStocks() {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: stockQueryKeys.all
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const invalidateAll = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = () => {
      queryClient.invalidateQueries({
        queryKey: [...stockQueryKeys.all, "list"]
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const invalidateList = t1;
  let t2;
  if ($[4] !== queryClient) {
    t2 = id => {
      queryClient.invalidateQueries({
        queryKey: stockQueryKeys.detail(id)
      });
    };
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const invalidateDetail = t2;
  let t3;
  if ($[6] !== invalidateAll || $[7] !== invalidateDetail || $[8] !== invalidateList) {
    t3 = {
      invalidateAll,
      invalidateList,
      invalidateDetail
    };
    $[6] = invalidateAll;
    $[7] = invalidateDetail;
    $[8] = invalidateList;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────

export function useLinkStockToItem() {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const {
    invalidateList,
    invalidateDetail
  } = useInvalidateStocks();
  let t0;
  if ($[0] !== invalidateDetail || $[1] !== invalidateList || $[2] !== queryClient) {
    t0 = stockId => {
      invalidateDetail(stockId);
      invalidateList();
      queryClient.invalidateQueries({
        queryKey: itemQueryKeys.all
      });
    };
    $[0] = invalidateDetail;
    $[1] = invalidateList;
    $[2] = queryClient;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const invalidateLinkedItemCaches = t0;
  let t1;
  if ($[4] !== queryClient) {
    t1 = async _newItemStock => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
    };
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== invalidateLinkedItemCaches) {
    t2 = (_data, _error, itemStock_0) => {
      invalidateLinkedItemCaches(itemStock_0.stockId);
    };
    $[6] = invalidateLinkedItemCaches;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = {
      mutationFn: _temp4,
      onMutate: t1,
      onSettled: t2
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useMutation(t3);
}
function _temp4(itemStock) {
  return linkStockToItem([itemStock]);
}
export function useUnlinkStockFromItem() {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const {
    invalidateList,
    invalidateDetail
  } = useInvalidateStocks();
  let t0;
  if ($[0] !== invalidateDetail || $[1] !== invalidateList || $[2] !== queryClient) {
    t0 = stockId => {
      invalidateDetail(stockId);
      invalidateList();
      queryClient.invalidateQueries({
        queryKey: itemQueryKeys.all
      });
    };
    $[0] = invalidateDetail;
    $[1] = invalidateList;
    $[2] = queryClient;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const invalidateLinkedItemCaches = t0;
  let t1;
  if ($[4] !== queryClient) {
    t1 = async () => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
    };
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== invalidateLinkedItemCaches) {
    t2 = (_data, _error, itemStock_0) => {
      invalidateLinkedItemCaches(itemStock_0.stockId);
    };
    $[6] = invalidateLinkedItemCaches;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = {
      mutationFn: _temp5,
      onMutate: t1,
      onSettled: t2
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useMutation(t3);
}
function _temp5(itemStock) {
  return unlinkStockFromItem([itemStock]);
}
export function useLinkSupplierToStock() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async payload_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const stockId = payload_0.stockId;
      const previousDetail = queryClient.getQueryData(stockQueryKeys.detail(stockId));
      return {
        previousDetail
      };
    };
    t1 = (_err, payload_1, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(payload_1.stockId), context.previousDetail);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== invalidateAll) {
    t2 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp6,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useMutation(t3);
}
function _temp6(payload) {
  return linkSupplierToStock([payload]);
}
export function useUnlinkSupplierFromStock() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async supplierToStock_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const previousDetail = queryClient.getQueryData(stockQueryKeys.detail(supplierToStock_0.stockId));
      if (previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(supplierToStock_0.stockId), old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            suppliers: old.suppliers?.filter(s => s.supplierId !== supplierToStock_0.supplierId)
          };
        });
      }
      return {
        previousDetail
      };
    };
    t1 = (_err, supplierToStock_1, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(supplierToStock_1.stockId), context.previousDetail);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== invalidateAll) {
    t2 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp7,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useMutation(t3);
}
function _temp7(supplierToStock) {
  return unlinkSupplierFromStock([supplierToStock]);
}
export function useArchiveStock() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  if ($[0] !== queryClient) {
    t0 = async id_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const listCache = queryClient.getQueriesData({
        queryKey: [...stockQueryKeys.all, "list"]
      });
      listCache.forEach(t1 => {
        const [queryKey, oldData] = t1;
        if (!oldData) {
          return;
        }
        queryClient.setQueryData(queryKey, removeStockFromCache(oldData, id_0));
      });
      return {
        listCache
      };
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== invalidateAll) {
    t1 = () => {
      invalidateAll();
    };
    $[2] = invalidateAll;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: _temp8,
      onMutate: t0,
      onSettled: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
}

/**
 * Payload for {@link useUpdateStock}. `category` and the level fields accept
 * `null` so the edit form can CLEAR them — the backend uses `exclude_unset`, so
 * an omitted key is a no-op while an explicit `null` clears the field. `note` is
 * recorded in the inventory log and is not a stock field.
 */
function _temp8(id) {
  return archiveStock(id);
}
export interface StockUpdateInput {
  id: string;
  name?: string;
  unit?: Unit;
  category?: string | null;
  minProviderAllocationLevel?: number | null;
  /** Each listed location is set; one with neither level is cleared. */
  locationLevels?: StockLocationLevel[];
  description?: string;
  notes?: string;
  note?: string;
}

/**
 * Updates a stock's core fields (name, unit, category, description, notes, and
 * the alert levels). It does NOT invalidate any queries — it writes the
 * server's response straight into the detail and list caches so the UI reflects
 * the edit without a refetch.
 */
export function useUpdateStock() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp9,
      onSuccess: (result, variables) => {
        const updated = result?.[0];
        if (!updated) {
          return;
        }
        queryClient.setQueryData(stockQueryKeys.detail(variables.id), old => old ? applyStockCoreFields(old, updated) : old);
        const listCaches = queryClient.getQueriesData({
          queryKey: [...stockQueryKeys.all, "list"]
        });
        listCaches.forEach(t1 => {
          const [key, data] = t1;
          if (!data) {
            return;
          }
          const filters = key[3] as StockFilters | undefined;
          queryClient.setQueryData(key, patchStockInCache(data, updated, filters?.locationIds));
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp9(update) {
  return updateStocks([update as Partial<Stock>]);
}
export function useCreateShipments() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async shipments_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const previousDetails = {};
      for (const shipment of shipments_0) {
        if (shipment.stockId && previousDetails[shipment.stockId] === undefined) {
          previousDetails[shipment.stockId] = queryClient.getQueryData(stockQueryKeys.detail(shipment.stockId));
        }
      }
      for (const shipment_0 of shipments_0) {
        if (!shipment_0.stockId) {
          continue;
        }
        queryClient.setQueryData(stockQueryKeys.detail(shipment_0.stockId), old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            shipments: [...(old.shipments || []), shipment_0 as Shipment]
          };
        });
      }
      return {
        previousDetails
      };
    };
    t1 = (_err, shipments_1, context) => {
      if (context?.previousDetails) {
        for (const [stockId, detail] of Object.entries(context.previousDetails)) {
          if (detail) {
            queryClient.setQueryData(stockQueryKeys.detail(stockId), detail);
          }
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== invalidateAll) {
    t2 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp10,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useMutation(t3);
}
function _temp10(shipments) {
  return createShipments(shipments);
}
export function useCreateShipment() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async shipment_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const previousDetail = queryClient.getQueryData(stockQueryKeys.detail(shipment_0.stockId));
      if (previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(shipment_0.stockId), old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            shipments: [...(old.shipments || []), shipment_0]
          };
        });
      }
      return {
        previousDetail
      };
    };
    t1 = (_err, shipment_1, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(shipment_1.stockId), context.previousDetail);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== invalidateAll) {
    t2 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp11,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useMutation(t3);
}
function _temp11(shipment) {
  return createShipments([shipment]);
}
export function useUpdateShipment() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async shipment_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const previousDetail = queryClient.getQueryData(stockQueryKeys.detail(shipment_0.stockId));
      if (previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(shipment_0.stockId), old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            shipments: old.shipments?.map(s => s.id === shipment_0.id ? shipment_0 : s)
          };
        });
      }
      return {
        previousDetail
      };
    };
    t1 = (_err, shipment_1, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(stockQueryKeys.detail(shipment_1.stockId), context.previousDetail);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== invalidateAll) {
    t2 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp12,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useMutation(t3);
}
function _temp12(shipment) {
  return updateShipments([shipment]);
}
export function useDeleteShipment() {
  const $ = _c(8);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  if ($[0] !== invalidateAll || $[1] !== queryClient) {
    t0 = deletedShipment => {
      if (deletedShipment) {
        queryClient.invalidateQueries({
          queryKey: stockQueryKeys.detail(deletedShipment.stockId)
        });
      } else {
        invalidateAll();
      }
    };
    $[0] = invalidateAll;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== invalidateAll) {
    t1 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: _temp13,
      onSuccess: t0,
      onSettled: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
function _temp13(shipmentId) {
  return deleteShipment(shipmentId);
}
export function useDrawdownStock() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async drawdown_0 => {
      await queryClient.cancelQueries({
        queryKey: stockQueryKeys.all
      });
      const previousDetail = queryClient.getQueryData(stockQueryKeys.detail(drawdown_0.stockId));
      return {
        previousDetail
      };
    };
    t1 = updatedStock => {
      if (updatedStock) {
        queryClient.setQueryData(stockQueryKeys.detail(updatedStock.id), old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            shipments: updatedStock.shipments
          };
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== invalidateAll) {
    t2 = () => {
      invalidateAll();
    };
    $[3] = invalidateAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp14,
      onMutate: t0,
      onSuccess: t1,
      onSettled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useMutation(t3);
}
function _temp14(drawdown) {
  return drawdownStock(drawdown);
}
export function useAddStock() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  const {
    invalidateAll
  } = useInvalidateStocks();
  let t0;
  if ($[0] !== queryClient) {
    t0 = updatedStock => {
      if (updatedStock) {
        queryClient.setQueryData(stockQueryKeys.detail(updatedStock.id), old => old && {
          ...old,
          shipments: updatedStock.shipments
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== invalidateAll) {
    t1 = () => {
      invalidateAll();
    };
    $[2] = invalidateAll;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: _temp15,
      onSuccess: t0,
      onSettled: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
}
function _temp15(addition) {
  return addStock(addition);
}